import { Component, EventEmitter, inject, Input, Output, signal, DestroyRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, take } from 'rxjs/operators';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { NotificationService } from 'src/app/notification.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';

@Component({
  selector: 'app-generate-video-procedure-option',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, TranslateModule, MatProgressSpinnerModule],
  template: `
    <div class="interactive-item" (click)="generateProcedure()">
      @if (isLoading()) {
        <mat-spinner diameter="20" class="interactive-item__icon"></mat-spinner>
      } @else {
        <mat-icon class="interactive-item__icon" aria-hidden="true">list_alt</mat-icon>
      }
      <span class="interactive-item__text">
        {{ 'ChunkController.Options.GenerateVideoProcedure' | translate }}
      </span>
    </div>
  `,
})
export class GenerateVideoProcedureOptionComponent {
  @Input() chunk!: Chunk;
  @Input() contentID = '';
  @Output() actionTriggered = new EventEmitter<void>();

  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private confirmService = inject(AppConfirmService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);

  isLoading = signal(false);

  generateProcedure() {
    if (this.isLoading()) return;

    this.confirmService
      .confirm({
        title: this.translate.instant('ChunkController.Options.GenerateVideoProcedure'),
        message: this.translate.instant('ChunkController.Options.GenerateVideoProcedureConfirm'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.isLoading.set(true);
          const contentId = this.contentID || (this.chunk as any).contentID || '';

          const trigger$ = this.chunkService.generateVideoProcedure(this.chunk._id, contentId);
          const update$ = this.notificationService.videoProcedureComplete$.pipe(
            filter((notification) => notification && notification.chunkId === this.chunk._id),
            take(1)
          );

          trigger$
            .pipe(
              switchMap(() => {
                this.toast.snackbar.open(this.translate.instant('ChunkController.Options.GenerateVideoProcedureStarted'), 'Ok', {
                  duration: 5000,
                });
                this.actionTriggered.emit(); // Dismiss the overlay early while it runs in background
                return update$;
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              next: () => {
                this.isLoading.set(false);
                this.toast.snackbar.open(
                  this.translate.instant('ChunkController.Options.GenerateVideoProcedureFinished') || 'Guide generated successfully!',
                  'Ok',
                  { duration: 3000 }
                );
              },
              error: () => {
                this.isLoading.set(false);
                this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
                  duration: 3000,
                });
              },
            });
        }
      });
  }
}
// Force recompile
